import * as React from "react"
import * as Yup from "yup"
import Form from "@modules/form/components/Form"
import { ConnectCmsFormProps } from "../types"
import { cmsIntegration as cmsIntegrationText } from "@modules/locales/default.js"
import { commonMessageTags, FormattedMessage } from "@modules/locales"
import { ConnectCmsFormControls } from "./ConnectCmsFormControls"
import { ConnectCmsFormLayout } from "./ConnectCmsFormLayout"
import { useCmsIntegrationsForSiteQuery } from "@modules/cms/shared/queries.generated"
import { InputConnectedField, Text } from "gatsby-interface"
import { useSetCmsIntegration } from "../hooks/useSetCmsIntegration"
import { CmsVendor } from "@modules/graphql/types"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import Loading from "@modules/ui/components/Loading"

type FormValues = {
  storeUrl: string
}

const validationSchema = Yup.object().shape<FormValues>({
  storeUrl: Yup.string().required(
    cmsIntegrationText.messages.validationShopifyShopNameRequired
  ),
})

export type ConnectShopifyFormProps = ConnectCmsFormProps

export function ConnectShopifyForm({
  siteId,
  siteName,
  onFetchError,
  onComplete,
  onCancel,
}: ConnectShopifyFormProps) {
  const { data, loading, error } = useCmsIntegrationsForSiteQuery({
    variables: {
      siteId,
    },
    onError: onFetchError,
  })

  const [setCmsIntegration] = useSetCmsIntegration({
    siteId,
  })

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading && data === undefined) {
    return (
      <Loading
        delay={1000}
        message={cmsIntegrationText.messages.loadingCosmicJsBuckets}
      />
    )
  }

  return (
    <Form<FormValues>
      initialValues={{
        storeUrl: "",
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        console.info(`CONNECT SHOPIFY FORM VALUES: `, values)
        return setCmsIntegration({
          variables: {
            config: {
              storeUrl: values.storeUrl,
            },
            siteId,
            vendor: CmsVendor.Shopify,
          },
        }).finally(onComplete)
      }}
      enableReinitialize
    >
      {/* TODO: Display confirmation page with details for this user */}
      {({ isSubmitting }) => {
        return (
          <ConnectCmsFormLayout
            description={
              <FormattedMessage<"siteName", "strong">
                message={
                  cmsIntegrationText.messages.shopifyConfirmIntegrationDetails
                }
                values={{ siteName }}
                tags={commonMessageTags}
              />
            }
            controls={
              <ConnectCmsFormControls
                isSubmitting={isSubmitting}
                onCancel={onCancel}
              />
            }
          >
            <React.Fragment>
              <Text size="S" css={{ margin: 0 }}>
                <FormattedMessage
                  message={
                    cmsIntegrationText.messages.shopifyStoreUrlInstructions
                  }
                />
              </Text>
              <InputConnectedField
                label={cmsIntegrationText.labels.shopifyStoreUrl}
                name="storeUrl"
                required
              />
            </React.Fragment>
          </ConnectCmsFormLayout>
        )
      }}
    </Form>
  )
}
